import { i18n } from '@/plugins/vue-i18n';
import { snakeCase } from 'lodash';
import { mapValues } from './utils';

export function translateAttribute(key, context = '') {
  // prepend context, for example from source type
  const originalPathArray = [...context.split('.'), ...key.split('.')].map(x =>
    // transform camelCased key to snake_cased translation key
    snakeCase(x)
  );

  const currentPath = originalPathArray.slice();
  const attr = currentPath.pop();

  // PERF: Work with reverse path so last item is removed per iteration instead of first item
  const recursiveLookup = dynamicPath => {
    const staticPath = dynamicPath.length >= 1 ? 'attributes' : 'general'; // TODO: support relationships[context]
    const path = [staticPath, ...dynamicPath, attr].join('.');
    if (i18n.te(path)) {
      return i18n.t(path);
    } // if translation exists, return translation
    if (dynamicPath.length === 0) {
      return i18n.t(originalPathArray.join('.'));
    } // if no fallbacks left, return original path

    return recursiveLookup(dynamicPath.slice(1)); // if no translation is found and fallbacks are available, try again
  };

  return recursiveLookup(currentPath);
}
export function translateFilter(key, context = '') {
  // prepend context, for example from source type
  const originalPathArray = [...context.split('.'), ...key.split('.')].map(x =>
    // transform camelCased key to snake_cased translation key
    snakeCase(x)
  );
  const currentPath = originalPathArray.slice();
  const attr = currentPath.pop();

  const attrWithouWithAndId = attr.replace(/^with_|_id$/g, '');

  var translationFileName = 'filters'; //default translation file name
  const contextPath = snakeCase(context);
  const attributeContextPath = contextPath + '.' + attr; // project.description

  // --- Filters ---
  let finalPath = translationFileName + '.' + attributeContextPath; //filters.project.with_description
  let finalPathWithoutWith = translationFileName + '.' + contextPath + '.' + attr.replace(/^with_/g, ''); //filters.project.description

  let finalPathNoContext = translationFileName + '.' + attr; //filters.with_description
  let finalPathNoContextWithoutWith = translationFileName + '.' + attr.replace(/^with_/g, ''); //filters.description

  // --- Attributes ---
  let finalAttirbutePath = 'attributes' + '.' + contextPath + '.' + attr.replace(/^with_/g, ''); //attirbutes.project.description

  // --- General ---
  let finalGeneralPath = 'general' + '.' + attr; //general.description

  // --- Relationships ---
  // find translation inside 'relationships' translation file, remove '_id' from attribute key so that the file is more readable
  let finalRelationshipPath = 'relationships' + '.' + contextPath + '.' + attrWithouWithAndId; //relationships.project.account

  // --- Resources ---
  let finalResourcePath = 'resources' + '.' + attrWithouWithAndId; //resources.account

  //check filters
  if (i18n.te(finalPath))
    return i18n.t(finalPath);
  else if (i18n.te(finalPathWithoutWith))
    return i18n.t(finalPathWithoutWith);
  else if (i18n.te(finalAttirbutePath))
    return i18n.t(finalAttirbutePath);
  else if (i18n.te(finalRelationshipPath))
    //by default use singular translation in case we use the format 'project | projects'
    return i18n.tc(finalRelationshipPath, 1);
  else if (i18n.te(finalResourcePath))
    //by default use singular translation in case we use the format 'project | projects'
    return i18n.tc(finalResourcePath, 1);
  else if (i18n.te(finalPathNoContext))
    return i18n.t(finalPathNoContext);
  else if (i18n.te(finalPathNoContextWithoutWith))
    return i18n.t(finalPathNoContextWithoutWith);
  else if (i18n.te(finalGeneralPath))
    return i18n.t(finalGeneralPath);
  else
    return finalPath;
}
export function translateAttributeForm(key, context = '') {
  // prepend context, for example from source type
  const originalPathArray = [...context.split('.'), ...key.split('.')].map(x =>
    // transform camelCased key to snake_cased translation key
    snakeCase(x)
  );
  const currentPath = originalPathArray.slice();
  const attr = currentPath.pop().replace(/_locales$/, '');
  const keyWithoutId = attr.replace(/_id$/, '');

  var translationFileName = 'attributes'; //default translation file name
  const attributeContextPath = snakeCase(context) + '.' + attr; // project.description

  // --- Attributes ---
  let finalPath = translationFileName + '.' + attributeContextPath; //attributes.project.description

  // --- General ---
  let finalGeneralPath = 'general' + '.' + attr;

  // --- Relationships ---
  // find translation inside 'relationships' translation file, remove '_id' from attribute key so that the file is more readable
  let finalRelationshipPath = 'relationships' + '.' + attributeContextPath;

  // --- Resources ---
  let finalResourcePath = 'resources' + '.' + keyWithoutId; //resources.account

  const withId = finalRelationshipPath.slice(-2) == 'id';
  if (withId)
    finalRelationshipPath = finalRelationshipPath.slice(0, -3);

  //if we can find the translation inside 'attributes' translation file, return it
  if (i18n.te(finalPath))
    return i18n.t(finalPath);
  else if (i18n.te(finalRelationshipPath))
    //by default use singular translation in case we use the format 'project | projects'
    return i18n.tc(finalRelationshipPath, 1);
  else if (i18n.te(finalResourcePath))
    //by default use singular translation in case we use the format 'project | projects'
    return i18n.tc(finalResourcePath, 1);
  else if (i18n.te(finalGeneralPath))
    return i18n.t(finalGeneralPath);
  else
    return finalPath;
}
export function translateValidator(validatorName, validatorParams = {}) {
  let { formatters, ...params } = validatorParams;

  if (formatters) {
    const formatter = (v, k) => (formatters?.[k] ? formatters[k](v) : v);
    params = mapValues(params, formatter);
  }

  return i18n.t(`validations.${validatorName}`, {
    ...params,
  });
}

export function translateValidationErrors(validation) {
  // the state of a property
  let errors = [];
  if (!validation.$error) return errors;
  Object.keys(validation)
    .filter(key => !validation[key] && /^[^$]/.test(key))
    .forEach(validatorName => {
      errors.push(
        translateValidator(validatorName, validation.$params[validatorName])
      );
    });
  return errors;
}

export function modelAttributeTranslation(model, attribute) {
  if (!model || !attribute) return '';
  const resource = snakeCase(model
    .split('::')
    .slice(-1)[0]);
  if (attribute.endsWith("_id")) {
    return this.$tc(`relationships.${resource}.${attribute.substring(0, attribute.length - 3)}`) + " ID";
  } else {
    return this.$t(`attributes.${resource}.${attribute}`);
  }
}

// Handled on backend
//
// export function translateLocalizedKey(localizedKey) {
//   const { locale, fallbackLocale } = i18n;
//   const definedLocales = Object.keys(localizedKey); // list from stringified object in database

//   let locales = [locale]; // build ordered list from i18n
//   if (fallbackLocale[locale]) locales = locales.concat(fallbackLocale[locale]);
//   locales = locales.concat(fallbackLocale.default);

//   return localizedKey[locales.find(l => definedLocales.includes(l))];
// }

export default {
  translateAttribute,
  translateFilter,
  translateValidator,
  translateValidationErrors,
  modelAttributeTranslation,
};
